/**
 * Source explorer client (task 4040.9): original inventory, parsed hierarchy, anchors, findings,
 * and access-checked previews from /api/v1/source-explorer.
 *
 * Three representations never mix: "original" (the vault bytes and their previews), "parsed"
 * (the immutable canonical parse), and "interpreted" (observations made from a region).
 */
import { apiFetch } from "../../app/api";

const BASE = "/api/v1/source-explorer";

export type AccessClassification = "public" | "internal" | "confidential" | "restricted";

export interface ContentAccess {
  allowed: boolean;
  access_classification: AccessClassification | string;
  reason: string;
  policy_version: string;
}

export interface SourceRights {
  rights_version: number;
  access_classification: AccessClassification;
  provider_exposure: "none" | "listed_providers" | "policy_permitted";
  permitted_providers: string[];
  permitted_processing_locations: string[];
  rag_corpus_allowed: boolean;
  verbatim_output_allowed: boolean;
  legal_hold: boolean;
  retention_until: string | null;
  records_status: string | null;
  license_terms: string | null;
}

export interface LatestParse {
  parsed_document_id: string;
  parser_name: string;
  parser_version: string;
  parse_status: "complete" | "partial";
  quarantined: boolean;
  incomplete: boolean;
}

export interface SourceRow {
  source_artifact_id: string;
  artifact_key: string;
  title: string;
  version_id: string;
  version: number;
  is_current: boolean;
  original_filename: string;
  sha256: string;
  byte_size: number;
  media_type: string | null;
  rights: SourceRights;
  content_access: ContentAccess;
  parses: number;
  latest_parse: LatestParse | null;
}

export interface SourceInventory {
  engagement_id: string;
  counts: {
    versions: number;
    sources: number;
    parsed: number;
    not_parsed: number;
    quarantined: number;
    incomplete: number;
    restricted: number;
  };
  filtered: number;
  offset: number;
  limit: number;
  items: SourceRow[];
}

export type PreviewKind = "page_image" | "text" | "none";

export interface ParseSummary {
  parsed_document_id: string;
  parser_name: string;
  parser_version: string;
  ocr_engine: string | null;
  ocr_version: string | null;
  fingerprint: string;
  parse_status: "complete" | "partial";
  page_count: number | null;
  output_sha256: string;
  representation_format: string;
  parsed_at: string;
  quarantined: boolean;
  incomplete: boolean;
  error_findings: number;
  warning_findings: number;
  info_findings: number;
}

export interface VersionDetail {
  representation: "original";
  version_id: string;
  source_artifact_id: string;
  engagement_id: string;
  artifact_key: string;
  title: string;
  version: number;
  is_current: boolean;
  original: {
    filename: string;
    path: string | null;
    sha256: string;
    extra_digests: Record<string, string>;
    byte_size: number;
    declared_media_type: string | null;
    signature: string | null;
    held: boolean;
    preview: PreviewKind;
  };
  provenance: {
    source_repository: string | null;
    retrieved_at: string | null;
    uploaded_by: string;
    intake_method: string;
    recorded_at: string;
  };
  rights: SourceRights;
  content_access: ContentAccess;
  parses: ParseSummary[];
}

export type Locator = Partial<{
  page_number: number;
  page_label: string;
  sheet_name: string;
  table_index: number;
  row_index: number;
  column_index: number;
  row_span: number;
  column_span: number;
  cell_ref: string;
  char_start: number;
  char_end: number;
  bbox: [number, number, number, number];
  coordinate_space: "pdf_points" | "pixels" | "normalized";
}>;

export interface FindingView {
  finding_id: string;
  finding_code: string;
  severity: "info" | "warning" | "error";
  action: "none" | "hold" | "quarantine";
  region_key: string | null;
  region_kind: string | null;
  locator: Locator;
  message: string | null;
  details: Record<string, unknown> | null;
  on_this_region?: boolean;
}

export interface ParsedDocumentView {
  representation: "parsed";
  parsed_document_id: string;
  source_artifact_version_id: string;
  parser: {
    name: string;
    version: string;
    ocr_engine: string | null;
    ocr_version: string | null;
    fingerprint: string;
    implementation_sha256: string | null;
    configuration: Record<string, unknown>;
  };
  representation_format: string;
  parse_status: "complete" | "partial";
  page_count: number | null;
  source_sha256: string;
  output_sha256: string;
  processing_started_at: string | null;
  parsed_at: string;
  region_count: number;
  quality: {
    quarantined: boolean;
    incomplete: boolean;
    error_findings: number;
    warning_findings: number;
    info_findings: number;
    policy_version: string;
  };
  sibling_outputs: {
    rules_version: string;
    text_units: number;
    knowledge_segment_units: number;
    transcription_units: number;
    caveats: Record<string, number>;
  } | null;
  selection: { registry: string } | null;
  metadata: Record<string, unknown> | null;
  content_access: ContentAccess;
  findings: FindingView[];
}

export interface RegionSummary {
  region_key: string;
  region_kind: string;
  ordinal: number | null;
  locator: Locator;
  is_hidden: boolean;
  child_count: number;
  finding_count: number;
  action: "none" | "hold" | "quarantine";
  condition: "clean" | "noted" | "degraded" | "impaired";
  label: string | null;
}

export interface RegionLevel {
  representation: "parsed";
  parsed_document_id: string;
  parent: string | null;
  total: number;
  offset: number;
  limit: number;
  content_access: ContentAccess;
  items: RegionSummary[];
}

export type SourceView =
  | {
      kind: "page_image";
      page: number;
      frame_key: string | null;
      frame_bbox: [number, number, number, number] | null;
      region_bbox: [number, number, number, number] | null;
      coordinate_space: string | null;
    }
  | {
      kind: "text";
      /** The region's (or nearest ancestor's) span in the original's bytes, when recorded. */
      byte_range: [number, number] | null;
      lines: [number, number] | null;
      range_key: string | null;
    }
  | { kind: "none"; reason: string };

export interface ObservationView {
  observation_id: string;
  representation: "transcribed" | "interpreted";
  mode: "transcription" | "interpretation";
  interpretation_kind: string | null;
  producer_kind: "parser" | "model" | "rule" | "human";
  producer_ref: string;
  producer_versions: Record<string, string>;
  transformation: string | null;
  inputs: string[];
  ai_proposal: boolean;
  recorded_at: string;
  label: string | null;
  transcribed_text: string | null;
  interpreted_value: unknown;
  unit_text: string | null;
}

export interface RegionDetail {
  parsed_document_id: string;
  source_artifact_version_id: string;
  region_key: string;
  region_kind: string;
  parent_key: string | null;
  ancestors: { region_key: string; region_kind: string }[];
  locator: Locator;
  is_hidden: boolean;
  child_count: number;
  parsed: {
    representation: "parsed";
    parser: string;
    text_content: string | null;
    displayed_value: string | null;
    formula: string | null;
    attributes: Record<string, unknown>;
    withheld: boolean;
  };
  quality: {
    action: "none" | "hold" | "quarantine";
    condition: "clean" | "noted" | "degraded" | "impaired";
    finding_ids: string[];
  };
  findings: FindingView[];
  observations: ObservationView[];
  source_view: SourceView;
  content_access: ContentAccess;
}

export interface TextExcerpt {
  representation: "original";
  offset: number;
  length: number;
  byte_size: number;
  truncated: boolean;
  text: string;
  /** The requested byte range as character offsets in ``text`` (null when not inside it). */
  mark: [number, number] | null;
  region_bytes: [number, number] | null;
}

export const EXCERPT_BYTES = 64 * 1024;

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

export function getSources(params: {
  engagement_id: string;
  q?: string;
  limit?: number;
  offset?: number;
}): Promise<SourceInventory> {
  return apiFetch<SourceInventory>(`${BASE}/sources${query(params)}`);
}

export function getVersion(versionId: string): Promise<VersionDetail> {
  return apiFetch<VersionDetail>(`${BASE}/versions/${encodeURIComponent(versionId)}`);
}

export function getParsedDocument(parsedDocumentId: string): Promise<ParsedDocumentView> {
  return apiFetch<ParsedDocumentView>(`${BASE}/parsed/${encodeURIComponent(parsedDocumentId)}`);
}

export function getRegions(
  parsedDocumentId: string,
  parent?: string,
  offset = 0,
): Promise<RegionLevel> {
  return apiFetch<RegionLevel>(
    `${BASE}/parsed/${encodeURIComponent(parsedDocumentId)}/regions${query({ parent, offset })}`,
  );
}

export function getRegion(parsedDocumentId: string, anchor: string): Promise<RegionDetail> {
  return apiFetch<RegionDetail>(
    `${BASE}/parsed/${encodeURIComponent(parsedDocumentId)}/region${query({ anchor })}`,
  );
}

/** A verbatim excerpt of a text original. With ``range`` (a region's bytes) the server opens
 * the excerpt just before it and marks it; ``offset`` pages through the file. */
export function getTextExcerpt(
  versionId: string,
  options: { offset?: number; range?: [number, number] | null } = {},
): Promise<TextExcerpt> {
  const [start, end] = options.range ?? [undefined, undefined];
  return apiFetch<TextExcerpt>(
    `${BASE}/versions/${encodeURIComponent(versionId)}/text${query({ offset: options.offset, start, end })}`,
  );
}

/** The verified original, always downloaded as an attachment (never rendered in this origin). */
export function originalUrl(versionId: string): string {
  return `${BASE}/versions/${encodeURIComponent(versionId)}/original`;
}

/** A PDF page or image frame rendered from the original as PNG. */
export function pageImageUrl(versionId: string, page: number): string {
  return `${BASE}/versions/${encodeURIComponent(versionId)}/pages/${page}/image`;
}

/** The region's box as percentages of its page or frame box (null when it has none). */
export function overlayBox(
  frame: [number, number, number, number] | null,
  region: [number, number, number, number] | null,
): { left: number; top: number; width: number; height: number } | null {
  if (!frame || !region) return null;
  const [fx0, fy0, fx1, fy1] = frame;
  const width = fx1 - fx0;
  const height = fy1 - fy0;
  if (width <= 0 || height <= 0) return null;
  const [x0, y0, x1, y1] = region;
  const pct = (value: number, span: number) => Math.min(100, Math.max(0, (value / span) * 100));
  return {
    left: pct(x0 - fx0, width),
    top: pct(y0 - fy0, height),
    width: pct(x1 - x0, width),
    height: pct(y1 - y0, height),
  };
}

/** The locator as recorded, in words: "page 3 · sheet Pumps · cell G42 · row index 2". Index
 * values are shown exactly as stored (zero-based), never renumbered. */
export function describeLocator(locator: Locator): string {
  const parts: string[] = [];
  if (locator.page_number !== undefined) {
    parts.push(
      `page ${locator.page_number}${locator.page_label && locator.page_label !== String(locator.page_number) ? ` (${locator.page_label})` : ""}`,
    );
  }
  if (locator.sheet_name !== undefined) parts.push(`sheet ${locator.sheet_name}`);
  if (locator.cell_ref !== undefined) parts.push(`cell ${locator.cell_ref}`);
  if (locator.table_index !== undefined) parts.push(`table index ${locator.table_index}`);
  if (locator.row_index !== undefined) parts.push(`row index ${locator.row_index}`);
  if (locator.column_index !== undefined) parts.push(`column index ${locator.column_index}`);
  if (locator.char_start !== undefined && locator.char_end !== undefined) {
    parts.push(`characters ${locator.char_start}–${locator.char_end}`);
  }
  if (locator.bbox && locator.coordinate_space) {
    parts.push(`box [${locator.bbox.map((v) => Math.round(v * 10) / 10).join(", ")}] ${locator.coordinate_space}`);
  }
  return parts.join(" · ");
}
